// mindmetal-app/web/src/components/mindmetal/CoverageIndicator.tsx
import { motion } from 'framer-motion'
import { cn, Badge } from '@personic/shared'

type CoverageLevel = 'minimal' | 'emerging' | 'established' | 'mature'

interface CoverageIndicatorProps {
  level: CoverageLevel
  observationCount?: number
  showProgress?: boolean
  className?: string
}

const COVERAGE_CONFIG: Record<
  CoverageLevel,
  {
    label: string
    color: 'default' | 'warning' | 'sage' | 'copper'
    progress: number
  }
> = {
  minimal: { label: 'Minimal', color: 'default', progress: 0.1 },
  emerging: { label: 'Emerging', color: 'warning', progress: 0.35 },
  established: { label: 'Established', color: 'sage', progress: 0.7 },
  mature: { label: 'Mature', color: 'copper', progress: 1 },
}

export function CoverageIndicator({
  level,
  observationCount,
  showProgress = true,
  className,
}: CoverageIndicatorProps) {
  const config = COVERAGE_CONFIG[level] || COVERAGE_CONFIG.minimal

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <Badge variant={config.color}>{config.label}</Badge>

      {showProgress && (
        <div className="flex-1 max-w-32">
          <div className="h-1.5 bg-warm-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-sage-400 to-copper-500 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${config.progress * 100}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
        </div>
      )}

      {observationCount !== undefined && (
        <span className="text-xs text-warm-500">{observationCount} obs</span>
      )}
    </div>
  )
}
